import React from "react";
import { Box, Typography } from "@mui/material";
import { formatMetric } from "../ranking-card/rankingCardMetrics";
import { getS3Url } from "../../utils/imageUtils";
import {
  AssetKicker,
  AssetTitle,
  Card,
  CardLabel,
  ChannelAvatar,
  DataTable,
  LinkedInAssetCanvas,
  MethodologyFooter,
  MUTED,
  SANS,
} from "./linkedinAssetChrome";
import {
  digitalInvestMapPoints,
  digitalInvestTop10,
  type DigitalInvestLivestreamInput,
  type DigitalInvestProgramInput,
} from "./linkedinDigitalInvestMap";

const CHANNEL_AVATAR_PX = 44;
const PALETTE = ["#7056EB", "#4ECDC4", "#F7B32B", "#FF6B6B", "#45B7D1", "#95E1A3"];

function formatLive(value: number): string {
  if (value <= 0) return "—";
  return formatMetric(value, "livestream-avg");
}

function formatRate(value: number): string {
  return `${(Math.round(value * 10) / 10).toFixed(1).replace(".", ",")}%`;
}

const LIVE_NOTE_MARGIN_TOP_PX = 8;
const LIVE_NOTE_FONT_PX = 13;
const LIVE_NOTE_LINE_HEIGHT = 1.35;
const LIVE_NOTE_HEIGHT_PX =
  LIVE_NOTE_MARGIN_TOP_PX + LIVE_NOTE_FONT_PX * LIVE_NOTE_LINE_HEIGHT;
const LIVE_NOTE_OFFSET_PX = LIVE_NOTE_HEIGHT_PX / 2;

const LinkedInDigitalInvestMapAsset: React.FC<{
  programs: DigitalInvestProgramInput[];
  livestreamRows: DigitalInvestLivestreamInput[];
  trackedProgramCount?: number;
}> = ({ programs, livestreamRows, trackedProgramCount }) => {
  const rows = digitalInvestTop10(
    digitalInvestMapPoints(programs, livestreamRows),
  );
  const trackedLabel = (
    trackedProgramCount ?? programs.length
  ).toLocaleString("es-CL");
  const tableColumns = `36px ${CHANNEL_AVATAR_PX}px minmax(0, 1.55fr) repeat(6, minmax(70px, 0.55fr))`;

  return (
    <LinkedInAssetCanvas>
      <Box>
        <AssetKicker>SEMANA 12–18 OCT · YOUTUBE · 100% DIGITAL</AssetKicker>
        <AssetTitle>
          Programas digitales:
          <br />
          ¿Dónde está la audiencia?
        </AssetTitle>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          transform: `translateY(${LIVE_NOTE_OFFSET_PX}px)`,
        }}
      >
          <Card sx={{ pr: "40px" }}>
            <CardLabel>Views, interacción, live y marcas</CardLabel>
            <DataTable
          columns={tableColumns}
          nameFontSize={22}
          metaFontSize={14}
          nameColumn={3}
          nameMetaGap={2}
          rowPy="14px"
          headers={[
            { label: "", align: "left", gridColumn: 1 },
            { label: "Programa", align: "left", gridColumn: "2 / 4" },
            {
              label: (
                <>
                  Views
                  <br />
                  promedio
                  <br />
                  7 días
                </>
              ),
              sorted: true,
              align: "right",
              gridColumn: 4,
            },
            {
              label: (
                <>
                  Interacción
                  <br />
                  promedio
                </>
              ),
              align: "right",
              gridColumn: 5,
            },
            {
              label: (
                <>
                  Tasa
                  <br />
                  interacción
                </>
              ),
              align: "right",
              gridColumn: 6,
            },
            {
              label: (
                <>
                  Live
                  <br />
                  promedio
                </>
              ),
              align: "right",
              gridColumn: 7,
            },
            {
              label: (
                <>
                  Live
                  <br />
                  peak
                </>
              ),
              align: "right",
              gridColumn: 8,
            },
            { label: "Marcas", align: "right", gridColumn: 9 },
          ]}
          rows={rows.map((row, index) => ({
            key: row.id,
            leading: (
              <Box
                sx={{
                  m: 0,
                  fontFamily: SANS,
                  fontWeight: 600,
                  fontSize: 22,
                  lineHeight: 1,
                  color: MUTED,
                  fontVariantNumeric: "tabular-nums",
                  textAlign: "left",
                }}
              >
                {index + 1}.
              </Box>
            ),
            avatar: (
              <ChannelAvatar
                src={row.imageS3Key ? getS3Url(row.imageS3Key) : ""}
                accentColor={PALETTE[index % PALETTE.length]!}
                size={CHANNEL_AVATAR_PX}
                sx={{ border: "none" }}
              />
            ),
            name: row.name,
            meta: row.channelName || undefined,
            cells: [
              formatMetric(row.views, "views"),
              formatMetric(row.interactions, "interactions"),
              formatRate(row.interactionRate),
              formatLive(row.liveViewers),
              formatLive(row.livePeak),
              String(row.brandCount),
            ],
          }))}
            />
          </Card>
          <Typography
            sx={{
              m: 0,
              mt: `${LIVE_NOTE_MARGIN_TOP_PX}px`,
              pr: "40px",
              fontFamily: SANS,
              fontWeight: 500,
              fontSize: LIVE_NOTE_FONT_PX,
              lineHeight: LIVE_NOTE_LINE_HEIGHT,
              color: MUTED,
              textAlign: "right",
            }}
          >
            — sin transmisión en vivo en el período.
          </Typography>
      </Box>

      <MethodologyFooter compact>
        {`: entre ${trackedLabel} programas 100% digitales, el top 10 por views on demand a 7 días de estreno. Interacción y tasa se leen sobre esas mismas views: likes y comentarios on demand, divididos por views. El live es la audiencia en transmisión, y las marcas son las que aparecen habladas, en placement físico, banners o cortinas.`}
      </MethodologyFooter>
    </LinkedInAssetCanvas>
  );
};

export default LinkedInDigitalInvestMapAsset;
